import {
  type DateFormat,
  describeRegnalYear,
  type FormattedDay,
  findRegnalYear,
  formatLegalDay,
  parseDateInput,
  parseRegnalCitation,
  type RegnalYear,
  type Reign,
  regnalYear,
  reignYearRange,
  resolveDate,
  type YearNote,
} from "@/lib/tool-kit/legal-styles/regnal-years";

/*
 * What the Regnal Year Converter shows for an input, as plain data: the widget only adds
 * words. Both directions are here so the page's worked examples and the tests call the same
 * code the widget does.
 */

export type CitationFailure =
  | { reason: "format" }
  | { reason: "reign"; name: string }
  | { reason: "range"; reign: Reign; first: number; last: number };

export type CitationResult =
  | { status: "empty" }
  | ({ status: "error" } & CitationFailure)
  | {
      status: "ok";
      reign: Reign;
      /** "13 Cha. 2", or "13 & 14 Cha. 2" for a session across two years. */
      citation: string;
      plainCitation: string;
      start: FormattedDay;
      end: FormattedDay;
      years: string;
      /** The citation spans two regnal years (a session). */
      session: boolean;
      /** The year or years are short because the reign began or ended inside them. */
      partial: boolean;
      /** The first and last years the reign had. */
      range: { first: number; last: number };
      notes: YearNote[];
    };

const RE_NAME_AFTER_NUMBER = /^\d{1,3}(?:\s*(?:&|and|-|–)\s*\d{1,3})?\s+(.*)$/i;

function failedReign(input: string): string {
  const match = RE_NAME_AFTER_NUMBER.exec(input.trim());
  return (match?.[1] ?? input).trim();
}

/** Regnal citation → the calendar days it covers. */
export function regnalToDates(
  input: string,
  format: DateFormat
): CitationResult {
  if (input.trim() === "") {
    return { status: "empty" };
  }
  const parsed = parseRegnalCitation(input);
  if (!parsed.ok) {
    if (parsed.reason === "reign") {
      return { status: "error", reason: "reign", name: failedReign(input) };
    }
    if (parsed.reason === "format") {
      return { status: "error", reason: "format" };
    }
    return {
      status: "error",
      reason: "range",
      reign: parsed.reign,
      ...reignYearRange(parsed.reign),
    };
  }
  const first = regnalYear(parsed.reign, parsed.first);
  const last = regnalYear(parsed.reign, parsed.last);
  if (!(first && last)) {
    return { status: "error", reason: "format" };
  }
  const whole = describeRegnalYear({ ...first, end: last.end }, format);
  const session = parsed.last !== parsed.first;
  const citation = session
    ? `${parsed.first} & ${parsed.last} ${parsed.reign.abbr}`
    : first.label;
  return {
    status: "ok",
    reign: parsed.reign,
    citation,
    plainCitation: citation.replaceAll(".", ""),
    start: whole.start,
    end: whole.end,
    years: whole.years,
    session,
    partial: first.partial || last.partial,
    range: reignYearRange(parsed.reign),
    notes: noteForYear(first),
  };
}

function noteForYear(year: RegnalYear): YearNote[] {
  const notes: YearNote[] = [];
  if (year.reign.id === "charles2" && year.number < 12) {
    notes.push("commonwealth");
  }
  if (year.reign.ascension) {
    notes.push("ascension");
  }
  if (year.reign.id === "william3") {
    notes.push("williamIii");
  }
  if (year.reign.pairs) {
    notes.push("philipMary");
  }
  if (year.reign.id === "edward3") {
    notes.push("edwardIiiFrance");
  }
  return notes;
}

export type DateFailure = "format" | "invalid" | "lost" | "dual" | "before";

export type DateResult =
  | { status: "empty" }
  | { status: "error"; reason: DateFailure }
  | {
      status: "interregnum";
      day: FormattedDay;
    }
  | {
      status: "ok";
      /** Falls in the years counted back from Charles II's claim, before the Restoration. */
      commonwealth: boolean;
      day: FormattedDay;
      reign: Reign;
      citation: string;
      plainCitation: string;
      start: FormattedDay;
      end: FormattedDay;
      years: string;
      notes: YearNote[];
    };

/** A calendar date → the regnal year it falls in. */
export function dateToRegnal(
  input: string,
  mode: "england" | "gregorian",
  format: DateFormat
): DateResult {
  if (input.trim() === "") {
    return { status: "empty" };
  }
  const parsed = parseDateInput(input);
  if (!parsed.ok) {
    return { status: "error", reason: "format" };
  }
  const resolved = resolveDate(parsed, mode);
  if (!resolved.ok) {
    return { status: "error", reason: resolved.reason };
  }
  const found = findRegnalYear(resolved.jdn);
  const day = formatLegalDay(resolved.jdn, format);
  if (found.kind === "before") {
    return { status: "error", reason: "before" };
  }
  if (found.kind === "interregnum") {
    return { status: "interregnum", day };
  }
  const description = describeRegnalYear(found.year, format);
  return {
    status: "ok",
    commonwealth: found.kind === "commonwealth",
    day,
    reign: found.year.reign,
    citation: found.year.label,
    plainCitation: found.year.plainLabel,
    start: description.start,
    end: description.end,
    years: description.years,
    notes: found.notes,
  };
}
